import { useState } from "react";
import { BENTO, PROFILE, STACK } from "../../data/portfolioData";
import { CopyIcon, CheckIcon } from "../ui/Icons";
import Reveal from "../ui/Reveal";

const FLOW = {
  y: {
    backgroundImage: "repeating-linear-gradient(to bottom, rgb(var(--c-accent)) 0 5px, transparent 5px 14px)",
    filter: "drop-shadow(0 0 3px rgb(var(--c-accent) / .9)) drop-shadow(0 0 6px rgb(var(--c-accent) / .4))",
  },
  x: {
    backgroundImage: "repeating-linear-gradient(to right, rgb(var(--c-accent)) 0 5px, transparent 5px 14px)",
    filter: "drop-shadow(0 0 3px rgb(var(--c-accent) / .9)) drop-shadow(0 0 6px rgb(var(--c-accent) / .4))",
  },
};

const Tile = ({ className = "", bodyClass = "justify-end", children, content }) => (
  <div
    className={`relative overflow-hidden rounded-3xl border border-ink/10 group/bento flex flex-col bg-[linear-gradient(90deg,rgb(var(--c-card))_0%,rgb(var(--c-card-2))_100%)] ${className}`}
  >
    {children}
    <div
      className={`tile-body relative z-10 flex flex-col md:h-full min-h-40 p-5 lg:p-10 group-hover/bento:translate-x-2 transition duration-200 ${bodyClass}`}
    >
      {content}
    </div>
  </div>
);

const Label = ({ children }) => (
  <div className="font-extralight md:max-w-32 md:text-xs lg:text-base text-sm text-ink/60">{children}</div>
);
const Title = ({ children, className = "max-w-96" }) => (
  <div className={`tile-title text-lg lg:text-3xl font-bold leading-snug ${className}`}>{children}</div>
);

// Stack chips; chips in the first column (except the last) get animated connector lines.
const StackColumns = () => (
  <div className="flex gap-1 lg:gap-5 w-fit absolute -right-3 lg:-right-2 top-1/2 -translate-y-1/2">
    {STACK.columns.map((col, c) => (
      <div key={c} className="flex flex-col gap-3 lg:gap-8">
        {c === 1 && <span className="lg:py-4 px-3 py-4 rounded-lg bg-blush" />}
        {col.map((item, i) => (
          <span
            key={item}
            className="relative lg:py-4 lg:px-3 py-2 px-3 text-xs lg:text-base opacity-50 lg:opacity-100 rounded-lg text-center bg-blush"
          >
            {item}
            {i < col.length - 1 && (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute left-1/2 top-full h-3 w-px -translate-x-1/2 lg:h-8 animate-flow-y"
                style={FLOW.y}
              />
            )}
            {c === 0 && i > 0 && (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute left-full top-1/2 h-px w-1 -translate-y-1/2 lg:w-5 animate-flow-x"
                style={FLOW.x}
              />
            )}
          </span>
        ))}
        {c === 0 && <span className="lg:py-4 px-3 py-4 rounded-lg bg-blush" />}
      </div>
    ))}
  </div>
);

// Scattered greetings, a few bright and the rest faint.
const GREETING_SPOTS = [
  "right-[24%] bottom-[10%] text-2xl lg:text-4xl text-ink/[0.10]",
  "right-[6%] top-[14%] text-2xl lg:text-4xl text-ink/[0.10]",
  "left-[6%] bottom-[15%] text-xl lg:text-4xl text-ink/[0.10]",
  "right-[9%] bottom-[32%] text-lg lg:text-2xl text-ink/[0.06]",
  "left-[43%] top-[8%] text-lg lg:text-2xl text-ink/[0.06]",
  "left-[9%] top-[42%] text-base lg:text-xl text-ink/[0.06]",
  "left-[31%] bottom-[7%] text-base lg:text-xl text-ink/[0.06]",
  "right-[39%] top-[42%] text-sm lg:text-lg text-ink/[0.06]",
  "right-[4%] top-[54%] text-sm lg:text-lg text-ink/[0.06]",
  "right-[20%] top-[6%] text-sm lg:text-lg text-ink/[0.06]",
  "left-[55%] bottom-[27%] text-xs lg:text-base text-ink/[0.06]",
  "right-[47%] bottom-[15%] text-xs lg:text-base text-ink/[0.06]",
];

const Greetings = () => (
  <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
    <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-accent/[0.06] blur-3xl" />
    <div className="absolute -bottom-20 left-1/4 h-56 w-56 rounded-full bg-accent/[0.05] blur-3xl" />
    {BENTO.greetings.map((word, i) => (
      <span key={word} className={`absolute select-none font-medium ${GREETING_SPOTS[i % GREETING_SPOTS.length]}`}>
        {word}
      </span>
    ))}
  </div>
);

const GradientBlobs = () => (
  <div className="absolute inset-0 overflow-hidden bg-[linear-gradient(40deg,#221e1c,#3b2a2f)]">
    <div className="h-full w-full blur-[40px]">
      {[
        ["rgba(232,69,122,0.8)", "animate-first", "center center"],
        ["rgba(246,207,220,0.7)", "animate-second", "calc(50% - 400px)"],
        ["rgba(214,56,107,0.7)", "animate-third", "calc(50% + 400px)"],
        ["rgba(240,160,130,0.6)", "animate-fourth opacity-70", "calc(50% - 200px)"],
        ["rgba(255,120,160,0.6)", "animate-fifth", "calc(50% - 800px) calc(50% + 800px)"],
      ].map(([color, anim, origin]) => (
        <div
          key={color}
          className={`absolute w-[80%] h-[80%] top-[10%] left-[10%] mix-blend-hard-light ${anim}`}
          style={{
            background: `radial-gradient(circle at center, ${color} 0, transparent 50%) no-repeat`,
            transformOrigin: origin,
          }}
        />
      ))}
    </div>
  </div>
);

const CopyEmailButton = () => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(PROFILE.email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      window.location.href = `mailto:${PROFILE.email}`;
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      className="relative inline-flex h-12 w-full max-w-72 md:mt-6 overflow-hidden rounded-lg p-[1px] focus:outline-none mx-auto"
    >
      <span className="absolute inset-[-1000%] animate-[spin_2s_linear_infinite] bg-[conic-gradient(from_90deg_at_50%_50%,#f6cfdc_0%,#e8457a_50%,#f6cfdc_100%)]" />
      <span className="inline-flex h-full w-full items-center justify-center rounded-lg bg-[#211d1b] px-7 text-sm font-medium text-[#f7ede4] backdrop-blur-3xl gap-2">
        {copied ? <CheckIcon size={16} /> : <CopyIcon size={16} />}
        {copied ? "Email is copied!" : "Copy my email address"}
      </span>
    </button>
  );
};

// "grid" is the stacked layout for normal vertical scrolling.
// "row" is a wide two-row strip used inside the horizontal intro.
const LAYOUTS = {
  grid: {
    section: "",
    grid: "grid grid-cols-1 md:grid-cols-6 lg:grid-cols-5 gap-4 lg:gap-8 mx-auto w-full py-20 md:auto-rows-[minmax(10rem,auto)]",
    hero: "lg:col-span-3 md:col-span-6 md:row-span-4 lg:min-h-[60vh]",
    greetings: "lg:col-span-2 md:col-span-3 md:row-span-2",
    stack: "lg:col-span-2 md:col-span-3 md:row-span-2",
    badge: "lg:col-span-2 md:col-span-3 md:row-span-1",
    scoop: "md:col-span-3 md:row-span-2",
    ask: "lg:col-span-2 md:col-span-3 md:row-span-1",
  },
  // Fits the whole grid in one viewport (used inside the horizontal intro).
  screen: {
    section: "h-full flex flex-col justify-center",
    grid:
      "grid grid-cols-6 grid-rows-3 gap-5 w-full h-[calc(100vh-10rem)] max-h-[820px] [&_.tile-body]:!p-7 [&_.tile-title]:!text-2xl",
    hero: "col-span-2 row-span-3",
    greetings: "col-start-3 col-span-2 row-start-1",
    stack: "col-start-5 col-span-2 row-start-1 row-span-2",
    badge: "col-start-3 col-span-2 row-start-2",
    scoop: "col-start-3 col-span-2 row-start-3",
    ask: "col-start-5 col-span-2 row-start-3",
  },
};

const Bento = ({ layout = "grid" }) => {
  const L = LAYOUTS[layout];
  return (
  <section id="about" className={L.section}>
    <Reveal>
      <div className={L.grid}>
        <Tile
          className={L.hero}
          content={<Title>{BENTO.hero}</Title>}
        >
          <img
            src={BENTO.photo}
            onError={(e) => {
              if (!e.currentTarget.src.endsWith(PROFILE.photo)) e.currentTarget.src = PROFILE.photo;
            }}
            alt=""
            className="absolute inset-0 w-full h-full object-cover object-center opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/40 to-transparent" />
        </Tile>

        <Tile
          className={L.greetings}
          bodyClass="justify-start"
          content={<Title>{BENTO.greetingsTitle}</Title>}
        >
          <Greetings />
        </Tile>

        <Tile
          className={L.stack}
          bodyClass="justify-center"
          content={
            <>
              <Label>{STACK.label}</Label>
              <Title className="max-w-[50%]">{STACK.headline}</Title>
            </>
          }
        >
          <div className="absolute inset-0 z-20 pointer-events-none">
            <StackColumns />
          </div>
        </Tile>

        <Tile className={L.badge} bodyClass="justify-start" content={<Title>{BENTO.badgeTitle}</Title>}>
          <div className="absolute inset-0 bg-grid opacity-70" />
          <div aria-hidden="true" className="absolute right-4 bottom-4 lg:right-6 lg:bottom-6 z-[5]">
            <div className="absolute inset-0 scale-110 rounded-full bg-accent/20 blur-xl" />
            <div className="relative h-12 w-12 lg:h-16 lg:w-16 rounded-full border-2 border-accent/70 flex items-center justify-center text-accent font-bold text-xs lg:text-sm tracking-wider">
              IEEE
            </div>
          </div>
        </Tile>

        <Tile
          className={L.scoop}
          bodyClass="justify-center md:justify-start lg:justify-center"
          content={
            <>
              <Label>{BENTO.scoopLabel}</Label>
              <Title>{BENTO.scoopTitle}</Title>
            </>
          }
        >
          <div className="absolute inset-0 bg-dots [mask-image:linear-gradient(to_left,black,transparent)]" />
          <div aria-hidden="true" className="absolute -right-6 -bottom-6 text-[9rem] lg:text-[12rem] leading-none opacity-20 select-none">
            🏊‍♀️
          </div>
        </Tile>

        <Tile
          className={L.ask}
          bodyClass="justify-center items-center text-center text-[#f7ede4]"
          content={
            <>
              <Title>{BENTO.askTitle}</Title>
              <div className="mt-5 relative w-full flex justify-center">
                <CopyEmailButton />
              </div>
            </>
          }
        >
          <GradientBlobs />
        </Tile>
      </div>
    </Reveal>
  </section>
  );
};

export default Bento;
